import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import {
  GanttFilter,
  GanttSort,
  Preferences,
  ResourceTypes,
  RetrieveWorkLoadsFilter,
  Sort as S,
} from "@planzone/dto";

import {
  useEditPreferences,
  useRetrieveConstraints,
  useRetrievePreferences,
  useRetrieveWbsMeta,
  useRetrieveWorkLoads,
} from "@planzone/view-models";

import { Flag, InfiniteScrollRoot } from "@common/infiniteScroll";
import { TreeToggle } from "@common/treeToggle";
import { Props } from "./types";
import { getDates, isToday, useResources, useRange } from "./utils";
import { useHover } from "./api/hover";
import { useViews, Views } from "./api/views";
import { useWorkloads } from "./api/workloads";
import { useDelays } from "./api/delays";
import { useDerivations } from "./api/derivations";
import { Header } from "./header";
import { Calendar } from "./calendar";
import { ScrollRoot, ScrollZone, ScrollBar } from "./scroll";
import { Context } from "./context";
import { createScrollPosStore } from "./scrollPosStore";
import { ScrollPosProvider } from "./scrollPosContext";
import styles from "./styles.scss";
import { useMessage } from "@components/useMessage";
import { events } from "@planzone/events";
import { WbsHeader } from "./wbsHeader";
import { DetailsView } from "./detailsView";
import { ZoomMenu } from "./zoomMenu";
import { Graph } from "./graph";
import { Wbs } from "./wbs";

function getFilter(
  namespace: string,
  preferences: Preferences,
  defaultFilter: GanttFilter,
): GanttFilter {
  try {
    return preferences[
      `${namespace}:workloadsFilter` as keyof typeof preferences
    ]
      ? JSON.parse(
          preferences[
            `${namespace}:workloadsFilter` as keyof typeof preferences
          ],
        )
      : defaultFilter;
  } catch (e) {
    return defaultFilter;
  }
}

function Workloads({
  zone,
  projects,
  range,
  filter: defaultFilter,
  sort: defaultSort,
  namespace,
  isFetchingProjects,
  canRetrieveProjects,
  retrieveProjects,
  selectSort,
}: Props) {
  const { preferences } = useRetrievePreferences();
  const { editPreferences } = useEditPreferences();

  const savedSort: GanttSort = (preferences[
    `${namespace}:workloadsSort` as keyof typeof preferences
  ] || defaultSort) as GanttSort;
  const sort = savedSort === "zone" ? "ascendingRank" : savedSort;
  const setSort = (nextSort: S) =>
    editPreferences({
      ...preferences,
      [`${namespace}:workloadsSort`]: nextSort,
    });
  const filter = getFilter(namespace, preferences, defaultFilter);
  const setFilter = (nextFilter: GanttFilter) =>
    editPreferences({
      ...preferences,
      [`${namespace}:workloadsFilter`]: JSON.stringify(nextFilter),
    });
  const [scrollPosStore] = React.useState(() => createScrollPosStore(0));
  const graphRef = React.useRef(null);
  // Pre-shift the visible range by ±1 month. This compensates getDates'
  // internal +1 day off-by-one — see workloads-date-audit.md §12.
  // Preserved literally; do not collapse without also touching getDates
  // (utils.ts).
  const dates = React.useMemo(
    () =>
      getDates(
        DateTime.fromISO(range.minDate)
          .minus({ months: 1 })
          .startOf("day")
          .toISO() ?? "",
        DateTime.fromISO(range.maxDate)
          .plus({ months: 1 })
          .startOf("day")
          .toISO() ?? "",
      ),
    [range.minDate, range.maxDate],
  );
  // Parallel DateTime[] for ruler + (later) positioning consumers.
  // Amortizes the per-cell Luxon parse cost flagged in the Phase 1 report —
  // one parse per visible day, not per render-per-cell.
  const datesDt = React.useMemo(
    () => dates.map((d) => DateTime.fromJSDate(d)),
    [dates],
  );
  const hover = useHover();
  const views = useViews();
  const workloads = useWorkloads();
  const delays = useDelays();
  const derivations = useDerivations();
  const {
    meta,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveWbsMeta,
  } = useRetrieveWbsMeta(projects);

  const { constraints } = useRetrieveConstraints();
  const [areDetailsOpen, setAreDetailsOpen] = React.useState(false);
  const [panelWidth, setPanelWidth] = React.useState<number>(30);
  const [elementsNbrMap, setElementsNbrMap] = React.useState<{
    [key: string]: number;
  }>({});
  const totalElementsNbr = Object.values(elementsNbrMap).reduce(
    (total, nbr) => total + nbr,
    0,
  );
  const { rangeRef, selectThumb } = useRange(panelWidth, setPanelWidth);
  const [toggledLists, setToggledLists] = React.useState<{
    [key: string]: boolean;
  }>({});

  const wbs = React.useRef<HTMLDivElement | null>(null);
  const graph = React.useRef<HTMLDivElement | null>(null);
  const background = React.useRef<HTMLDivElement | null>(null);
  const horizontalScrollBar = React.useRef<HTMLDivElement | null>(null);
  const paddingTop =
    views.cell.height +
    1 +
    (views.currentView === Views.DAILY
      ? views.cell.height * 2
      : views.cell.height + views.cell.height * (1 / 3));

  // Zoom changes cell.width; the same scrollLeft now maps to a different chunk
  // index. Re-derive before paint so Calendar's chunk window stays aligned.
  React.useLayoutEffect(() => {
    if (graph.current) {
      scrollPosStore.set(
        Math.round(graph.current.scrollLeft / views.cell.width),
      );
    }
  }, [views.cell.width, scrollPosStore]);

  const { resources } = useResources();
  const workloadsFilter = {
    ...(resources.length > 0
      ? {
          resources: resources,
        }
      : {}),
    view: (views.currentView === Views.YEARLY
      ? "year"
      : views.currentView) as RetrieveWorkLoadsFilter["view"],
    startDate: datesDt[0].toUTC().toISO() ?? "",
    endDate: datesDt[datesDt.length - 1].toUTC().toISO() ?? "",
  };
  const {
    isRequestPending: isRetrieveWorkLoadsPending,
    isRequestSuccess: isRetrieveWorkLoadsSuccess,
    isRequestFailure: isRetrieveWorkLoadsFailure,
    isDirty,
    workLoads,
    retrieveWorkLoads,
  } = useRetrieveWorkLoads(workloadsFilter);

  React.useEffect(() => {
    ((!isRetrieveWorkLoadsPending &&
      !isRetrieveWorkLoadsFailure &&
      !isRetrieveWorkLoadsSuccess) ||
      (isDirty && !isRequestPending)) &&
      retrieveWorkLoads(workloadsFilter);
  }, [
    workloadsFilter.startDate,
    workloadsFilter.endDate,
    workloadsFilter.view,
    isDirty,
  ]);

  React.useEffect(() => {
    !isFetchingProjects &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveWbsMeta({ projects });
  }, [isFetchingProjects]);

  React.useEffect(() => {
    selectSort && selectSort(savedSort);
  }, [savedSort, selectSort]);

  useMessage(
    ({ type }) =>
      (type === events.moveListElementSucceeded ||
        ["Task", "List", "Milestone", "Event"].find(
          (elementType) =>
            type === `create${elementType}Succeeded` ||
            type === `remove${elementType}Succeeded`,
        ) ||
        type === events.editAllocationSucceeded ||
        type === events.removeAllocationSucceeded ||
        type === events.allocateResourcesSucceeded) &&
      retrieveWbsMeta({ projects }),
  );

  return (
    <ScrollPosProvider value={scrollPosStore}>
      <Context.Provider
        value={{
          ...hover,
          ...views,
          ...workloads,
          ...delays,
          ...derivations,
          elementsNbrMap,
          setProjectElementsNbr: (id, nbr) =>
            elementsNbrMap[id] !== nbr &&
            setElementsNbrMap((m) => ({ ...m, [id]: nbr })),
        }}
      >
        <TreeToggle>
          <div className={styles.gantt}>
            <div className={styles.section}>
              <ScrollRoot>
                <InfiniteScrollRoot canFetchOnIdle>
                  <div
                    className={styles.layer}
                    ref={background}
                    style={{
                      left: `${panelWidth}%`,
                      overflow: "hidden",
                    }}
                  >
                    <Calendar
                      dates={datesDt}
                      cell={views.cell}
                      currentView={views.currentView}
                      height={totalElementsNbr * views.cell.height}
                    />
                  </div>
                  <div className={styles.layer}>
                    <div
                      ref={rangeRef}
                      className={styles.grid}
                      style={{
                        gridTemplateColumns: `${panelWidth}% 1fr`,
                      }}
                    >
                      <div className={styles.cell}>
                        <ScrollZone id="projectElements" orientation="vertical">
                          <ScrollZone id="wbs" orientation="horizontal">
                            <ScrollBar
                              id="wbs"
                              scrollSize={areDetailsOpen ? 720 : 460}
                              orientation="horizontal"
                              scroll={(scrollPos) => {
                                if (wbs.current) {
                                  wbs.current.scrollLeft = scrollPos;
                                }
                              }}
                            >
                              <div
                                ref={wbs}
                                id="workloads"
                                className={styles.leftPanel}
                              >
                                <div
                                  style={{
                                    minHeight:
                                      totalElementsNbr * views.cell.height +
                                      paddingTop +
                                      45,
                                  }}
                                >
                                  <WbsHeader
                                    isOpen={areDetailsOpen}
                                    toggle={() => {
                                      setPanelWidth(
                                        !areDetailsOpen
                                          ? panelWidth + 30
                                          : panelWidth - 30,
                                      );
                                      setAreDetailsOpen(!areDetailsOpen);
                                    }}
                                  />
                                  <div
                                    className={classNames(styles.row, {
                                      [styles.openedDetails]: areDetailsOpen,
                                    })}
                                  >
                                    <div className={styles.wbs}>
                                      <Wbs
                                        workLoads={workLoads}
                                        projects={projects}
                                        filter={filter}
                                        sort={sort}
                                        zone={zone}
                                        toggledLists={toggledLists}
                                        meta={meta}
                                        style={{
                                          itemHeight: 30,
                                          colWidth: "4.5rem",
                                          indentSize: "3rem",
                                        }}
                                        {...hover}
                                        loaderId="workloads"
                                        configuration={[{ key: "allocations" }]}
                                        toggleList={(
                                          uuid,
                                          resourceUuid,
                                          isOpen,
                                        ) =>
                                          setToggledLists((s) => ({
                                            ...s,
                                            [`${resourceUuid}:${uuid}`]: isOpen,
                                          }))
                                        }
                                        toggleResource={(uuid, isOpen) =>
                                          setToggledLists((s) => ({
                                            ...s,
                                            [uuid]: isOpen,
                                          }))
                                        }
                                        setProjectElementsNbr={(id, nbr) =>
                                          elementsNbrMap[id] !== nbr &&
                                          setElementsNbrMap((m) => ({
                                            ...m,
                                            [id]: nbr,
                                          }))
                                        }
                                        canRetrieveProjects={
                                          canRetrieveProjects
                                        }
                                        retrieveProjects={retrieveProjects}
                                        isFetchingProjects={isFetchingProjects}
                                        isFetchingWorkloads={
                                          isRetrieveWorkLoadsPending
                                        }
                                      />
                                      <Flag
                                        name="projects"
                                        isDisabled={!canRetrieveProjects}
                                        retrieveItems={retrieveProjects}
                                      />
                                    </div>
                                    {areDetailsOpen && (
                                      <DetailsView
                                        workLoads={workLoads}
                                        projects={projects}
                                        filter={filter}
                                        sort={sort}
                                        zone={zone}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </ScrollBar>
                          </ScrollZone>
                        </ScrollZone>
                      </div>
                      <div className={styles.cell}>
                        <div className={styles.zoomMenu}>
                          <ZoomMenu
                            zoom={views.zoom}
                            zoomIn={() => views.setZoom(views.zoom + 1)}
                            zoomOut={() =>
                              views.setZoom(
                                views.zoom <= 0
                                  ? Math.max(views.zoom - 0.5, -9.5)
                                  : views.zoom - 1,
                              )
                            }
                          />
                        </div>
                        <ScrollZone id="graph" orientation="horizontal">
                          <ScrollBar
                            id="projectElements"
                            orientation="vertical"
                            scrollSize={
                              totalElementsNbr * views.cell.height +
                              paddingTop +
                              45
                            }
                            withInfiniteLoad
                            scroll={(scrollPos) => {
                              if (wbs.current) {
                                wbs.current.scrollTop = scrollPos;
                              }

                              if (graph.current) {
                                graph.current.scrollTop = scrollPos;
                              }
                            }}
                          >
                            <ScrollBar
                              id="graph"
                              orientation="horizontal"
                              scrollSize={dates.length * views.cell.width}
                              scroll={(scrollPos) => {
                                if (background.current) {
                                  background.current.scrollLeft = scrollPos;
                                }

                                if (graph.current) {
                                  graph.current.scrollLeft = scrollPos;
                                }

                                requestAnimationFrame(() =>
                                  scrollPosStore.set(
                                    Math.round(scrollPos / views.cell.width),
                                  ),
                                );
                              }}
                              updateRef={(ref) => {
                                horizontalScrollBar.current = ref.current;
                              }}
                            >
                              <ScrollZone
                                id="projectElements"
                                orientation="vertical"
                              >
                                <div
                                  ref={graph}
                                  className={styles.graph}
                                  style={{
                                    marginTop: paddingTop,
                                    minHeight: `calc(100% - ${paddingTop + 30}px)`,
                                  }}
                                >
                                  <div
                                    style={{
                                      minHeight:
                                        totalElementsNbr * views.cell.height +
                                        paddingTop +
                                        45,
                                      marginTop: (paddingTop + 45) * -1,
                                      height: 0,
                                    }}
                                  >
                                    <Graph
                                      ref={graphRef}
                                      workLoads={workLoads}
                                      projects={projects}
                                      filter={filter}
                                      sort={sort}
                                      zone={zone}
                                      toggledLists={toggledLists}
                                      constraints={constraints}
                                      dates={datesDt}
                                      graphRef={graphRef}
                                      gantt={[]}
                                    />
                                  </div>
                                </div>
                              </ScrollZone>
                            </ScrollBar>
                          </ScrollBar>
                        </ScrollZone>
                      </div>
                      <div
                        className={styles.track}
                        style={{ left: `calc(${panelWidth}% - 0.5px)` }}
                      >
                        <div
                          className={styles.thumb}
                          onMouseDown={selectThumb}
                          onTouchStart={selectThumb}
                        >
                          <svg className={styles.thumbIcon} viewBox="0 0 7 14">
                            <line
                              y2="12"
                              transform="translate(6 1)"
                              strokeLinecap="round"
                              strokeWidth="2"
                            />
                            <line
                              y2="12"
                              transform="translate(1 1)"
                              strokeLinecap="round"
                              strokeWidth="2"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </InfiniteScrollRoot>
              </ScrollRoot>
            </div>
            <Header
              project={projects.length === 1 ? projects[0] : undefined}
              ganttLength={totalElementsNbr}
              filter={filter}
              sort={savedSort}
              moveToToday={() => {
                const todayIndex = dates.findIndex(isToday);

                horizontalScrollBar.current &&
                  horizontalScrollBar.current.scrollTo({
                    top: 0,
                    left: todayIndex * views.cell.width,
                    behavior: "smooth",
                  });
              }}
              setFilter={setFilter}
              setSort={setSort}
              reset={() => {
                editPreferences({
                  ...preferences,
                  [`${namespace}:workloadsFilter`]:
                    JSON.stringify(defaultFilter),
                  [`${namespace}:workloadsSort`]: defaultSort,
                });
              }}
            />
          </div>
        </TreeToggle>
      </Context.Provider>
    </ScrollPosProvider>
  );
}

export { Workloads };
